// Import global CSS before App to ensure any library CSS is specified in the correct order
import '../global.scss';
import React from 'react';
import ReactDOM from 'react-dom';
import { ApolloProvider } from '@apollo/client';
import { createBrowserHistory } from 'history';
import { Router } from 'react-router-dom';
import { loadableReady } from '@loadable/component';
import { FeatureFlagProvider, FeatureFlagResults } from '@moda/portal-stanchions';
import { loadSegment } from '@moda/portal-astrarium';
import { HelmetProvider } from 'react-helmet-async';
import Perfume from 'perfume.js';
import { CONFIG } from '../config';
import { identifyUser } from '../lib/identifyUser';
import { trackFeatureFlagView, clearFeatureFlagTrackingCache } from '../lib/trackFeatureFlagView';
import { createClientSideApolloClient } from '../lib/apollo/client';
import { isDevelopment } from '../lib/isEnv';
import { tc } from '../lib/trackingContext';
import { makeGoogleAnalyticsAdjustments } from '../lib/googleAnalytics/googleAnalyticsAdjustments';
import { getUserFromCookies } from '../hooks/useUser';
import { BreakpointProvider } from '../components/BreakpointProvider';
import { App } from '../App';

// TODO: move this polyfill to it's own chunk and load it only when required
import 'intersection-observer';
import 'isomorphic-fetch';
import 'focus-visible';

// For some reason, webpack is ignoring this file if we use `import` instead of `require`.
require('../../vendor/crownpeak');

declare global {
  interface Window {
    __FEATURE_FLAGS__?: FeatureFlagResults;
    __SERVER_ERROR__?: boolean;
    __SSR_WIDTH__?: number;
  }
}

const el = document.getElementById('root');

const history = createBrowserHistory();
const client = createClientSideApolloClient(history);

history.listen(() => clearFeatureFlagTrackingCache());

// If the user has declined the evidon prompt, don't load GTM, but load everything else.
// If the user has accepted the evidon prompt, or has not decided yet, load everything.
// If the user declines, the page will hard-refresh (in the evidon callback),
// and we'll see the decline cookie on reload.
// If the user closes the prompt without answering, it will stay hidden until the next refresh,
// but is otherwise effectively the same as accepting.
// We need to do it this way, because there's no way to unload an integration
// once Segment loads it.
if (document.cookie.includes('_evidon_suppress_notification_cookie')) {
  loadSegment(CONFIG.SEGMENT_WRITE_KEY, {
    integrations: {
      All: false,
      'Segment.io': true,
      'Google Analytics': true
    }
  });
} else {
  loadSegment(CONFIG.SEGMENT_WRITE_KEY, { integrations: { All: true } });
}

makeGoogleAnalyticsAdjustments();

identifyUser(getUserFromCookies());

const events = {
  cls: 'Cumulative Layout Shift',
  lcp: 'Largest Contentful Paint',
  fidVitals: 'First Input Delay'
} as const;
function isValidEvent(event: string): event is keyof typeof events {
  return Object.keys(events).includes(event);
}

try {
  new Perfume({
    analyticsTracker: options => {
      const { data, metricName, navigatorInformation, vitalsScore } = options;
      const eventData = {
        category: 'Performance',
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        value: metricName === 'cls' ? Math.round(Number(data) * 1000) : data,
        vitalsScore,
        label: navigatorInformation.isLowEndDevice ? 'Low End Device' : 'High End Device',
        nonInteraction: 1
      };
      if (isValidEvent(metricName)) {
        tc.ready(() => {
          tc.track(events[metricName], { addToTrackingEvent: eventData });
        });
      }
    }
  });
} catch (e) {
  // Intentionally doing nothing here.
}

const ClientSideApp = (
  <FeatureFlagProvider
    flags={window.__FEATURE_FLAGS__ || {}}
    onFlagRequested={trackFeatureFlagView}
  >
    <Router history={history}>
      <ApolloProvider client={client}>
        <HelmetProvider>
          <BreakpointProvider ssrWidth={window.__SSR_WIDTH__}>
            <App error={window.__SERVER_ERROR__} />
          </BreakpointProvider>
        </HelmetProvider>
      </ApolloProvider>
    </Router>
  </FeatureFlagProvider>
);

const init = () =>
  isDevelopment()
    ? ReactDOM.render(ClientSideApp, el)
    : loadableReady(() => ReactDOM.hydrate(ClientSideApp, el));

init();
